'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ChevronRight, ChevronLeft, Check, Star, Shield, Zap, Circle } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  businessName: string;
  businessCategory: string;
  businessAddress: string;
  businessPostcode: string;
  businessPhone: string;
  businessWebsite: string;
  tier: string;
  agreeTerms: boolean;
}

const tiers = [
  { id: 'reg-tier-free', value: 'free', label: 'Free', price: '£0/mo', icon: Circle, desc: 'Basic name listing only', color: 'border-border' },
  { id: 'reg-tier-standard', value: 'standard', label: 'Standard', price: '£9.99/mo', icon: Zap, desc: 'Full business page', color: 'border-info/40' },
  { id: 'reg-tier-enhanced', value: 'enhanced', label: 'Enhanced', price: '£24.99/mo', icon: Shield, desc: 'Council verified', color: 'border-purple-300' },
  { id: 'reg-tier-featured', value: 'featured', label: 'Featured', price: '£49.99/mo', icon: Star, desc: 'Top placement + analytics', color: 'border-warning/60' },
];

const categories = [
  'Trades & Construction', 'Restaurants & Cafés', 'Health & Medical',
  'Legal Services', 'Accountants & Finance', 'Estate Agents & Property',
  'Automotive', 'Hair & Beauty', 'Garden & Landscaping', 'Retail & Shopping',
  'Fitness & Sport', 'Education & Tutoring', 'Hotels & Accommodation',
  'Photography & Media', 'Removal & Storage', 'IT & Technology',
];

export default function RegisterForm({ onSwitchToLogin }: { onSwitchToLogin: () => void }) {
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<RegisterFormData>({ defaultValues: { tier: 'standard' } });

  const selectedTier = watch('tier');
  const password = watch('password');

  const nextStep = async () => {
    const fieldsToValidate: (keyof RegisterFormData)[] =
      step === 1
        ? ['firstName', 'lastName', 'email', 'password', 'confirmPassword']
        : step === 2
        ? ['businessName', 'businessCategory', 'businessAddress', 'businessPostcode']
        : [];
    const valid = await trigger(fieldsToValidate);
    if (valid) setStep(step + 1);
  };

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    // Backend integration point: POST /api/auth/register
    await new Promise((r) => setTimeout(r, 1500));
    toast.success('Registration successful! Welcome to the West Suffolk Business Directory.');
    router.push('/business-listing-management');
    setIsLoading(false);
  };

  const stepLabels = ['Account Details', 'Business Info', 'Choose Plan'];

  return (
    <div>
      <h2 className="text-xl font-bold text-foreground mb-1">Register your business</h2>
      <p className="text-sm text-muted-foreground mb-5">
        Get listed in the West Suffolk Business Directory today.
      </p>

      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-7">
        {stepLabels.map((label, i) => (
          <React.Fragment key={`step-indicator-${i + 1}`}>
            <div className="flex flex-col items-center">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  i + 1 < step
                    ? 'bg-positive text-white'
                    : i + 1 === step
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {i + 1 < step ? <Check size={13} /> : i + 1}
              </div>
              <span className={`text-xs mt-1 font-semibold whitespace-nowrap ${i + 1 === step ? 'text-foreground' : 'text-muted-foreground'}`}>
                {label}
              </span>
            </div>
            {i < stepLabels.length - 1 && (
              <div className={`flex-1 h-0.5 mb-4 mx-1 transition-colors ${i + 1 < step ? 'bg-positive' : 'bg-border'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Step 1: Account details */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="reg-firstName" className="block text-sm font-semibold text-foreground mb-1.5">
                  First name <span className="text-negative">*</span>
                </label>
                <input
                  id="reg-firstName"
                  type="text"
                  className={`input-field ${errors.firstName ? 'border-negative' : ''}`}
                  {...register('firstName', { required: 'Required' })}
                />
                {errors.firstName && <p className="text-negative text-xs mt-1">{errors.firstName.message}</p>}
              </div>
              <div>
                <label htmlFor="reg-lastName" className="block text-sm font-semibold text-foreground mb-1.5">
                  Last name <span className="text-negative">*</span>
                </label>
                <input
                  id="reg-lastName"
                  type="text"
                  className={`input-field ${errors.lastName ? 'border-negative' : ''}`}
                  {...register('lastName', { required: 'Required' })}
                />
                {errors.lastName && <p className="text-negative text-xs mt-1">{errors.lastName.message}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="reg-email" className="block text-sm font-semibold text-foreground mb-1.5">
                Email address <span className="text-negative">*</span>
              </label>
              <input
                id="reg-email"
                type="email"
                autoComplete="email"
                className={`input-field ${errors.email ? 'border-negative' : ''}`}
                placeholder="you@yourbusiness.co.uk"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
              />
              {errors.email && <p className="text-negative text-xs mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="reg-phone" className="block text-sm font-semibold text-foreground mb-1.5">
                Mobile number
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Used for account security — not displayed publicly</p>
              <input
                id="reg-phone"
                type="tel"
                className="input-field"
                placeholder="07xxx xxxxxx"
                {...register('phone')}
              />
            </div>

            <div>
              <label htmlFor="reg-password" className="block text-sm font-semibold text-foreground mb-1.5">
                Password <span className="text-negative">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters, include a number and symbol</p>
              <div className="relative">
                <input
                  id="reg-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={`input-field pr-10 ${errors.password ? 'border-negative' : ''}`}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Must be at least 8 characters' },
                    pattern: { value: /(?=.*[0-9])(?=.*[!@#$%^&*])/, message: 'Must include a number and symbol' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p className="text-negative text-xs mt-1">{errors.password.message}</p>}
            </div>

            <div>
              <label htmlFor="reg-confirm" className="block text-sm font-semibold text-foreground mb-1.5">
                Confirm password <span className="text-negative">*</span>
              </label>
              <input
                id="reg-confirm"
                type="password"
                autoComplete="new-password"
                className={`input-field ${errors.confirmPassword ? 'border-negative' : ''}`}
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) => value === password || 'Passwords do not match',
                })}
              />
              {errors.confirmPassword && <p className="text-negative text-xs mt-1">{errors.confirmPassword.message}</p>}
            </div>
          </div>
        )}

        {/* Step 2: Business info */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label htmlFor="reg-bizName" className="block text-sm font-semibold text-foreground mb-1.5">
                Business name <span className="text-negative">*</span>
              </label>
              <input
                id="reg-bizName"
                type="text"
                className={`input-field ${errors.businessName ? 'border-negative' : ''}`}
                placeholder="e.g. Sutton Builders Ltd"
                {...register('businessName', { required: 'Business name is required' })}
              />
              {errors.businessName && <p className="text-negative text-xs mt-1">{errors.businessName.message}</p>}
            </div>

            <div>
              <label htmlFor="reg-bizCat" className="block text-sm font-semibold text-foreground mb-1.5">
                Business category <span className="text-negative">*</span>
              </label>
              <select
                id="reg-bizCat"
                className={`input-field ${errors.businessCategory ? 'border-negative' : ''}`}
                {...register('businessCategory', { required: 'Please select a category' })}
              >
                <option value="">Select a category...</option>
                {categories.map((cat) => (
                  <option key={`reg-cat-${cat}`} value={cat}>{cat}</option>
                ))}
              </select>
              {errors.businessCategory && <p className="text-negative text-xs mt-1">{errors.businessCategory.message}</p>}
            </div>

            <div>
              <label htmlFor="reg-bizAddr" className="block text-sm font-semibold text-foreground mb-1.5">
                Business address <span className="text-negative">*</span>
              </label>
              <input
                id="reg-bizAddr"
                type="text"
                className={`input-field ${errors.businessAddress ? 'border-negative' : ''}`}
                placeholder="Street address"
                {...register('businessAddress', { required: 'Address is required' })}
              />
              {errors.businessAddress && <p className="text-negative text-xs mt-1">{errors.businessAddress.message}</p>}
            </div>

            <div>
              <label htmlFor="reg-bizPost" className="block text-sm font-semibold text-foreground mb-1.5">
                Postcode <span className="text-negative">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Must be a West Suffolk postcode (IP, CB, CO, NR)</p>
              <input
                id="reg-bizPost"
                type="text"
                className={`input-field uppercase ${errors.businessPostcode ? 'border-negative' : ''}`}
                placeholder="IP33 1AB"
                {...register('businessPostcode', {
                  required: 'Postcode is required',
                  pattern: {
                    value: /^(IP|CB|CO|NR)[0-9]/i,
                    message: 'Must be a West Suffolk postcode (IP, CB, CO, NR)',
                  },
                })}
              />
              {errors.businessPostcode && <p className="text-negative text-xs mt-1">{errors.businessPostcode.message}</p>}
            </div>

            <div>
              <label htmlFor="reg-bizPhone" className="block text-sm font-semibold text-foreground mb-1.5">
                Business phone number
              </label>
              <input
                id="reg-bizPhone"
                type="tel"
                className="input-field"
                placeholder="01284 xxxxxx"
                {...register('businessPhone')}
              />
            </div>

            <div>
              <label htmlFor="reg-bizWeb" className="block text-sm font-semibold text-foreground mb-1.5">
                Business website
              </label>
              <input
                id="reg-bizWeb"
                type="url"
                className="input-field"
                placeholder="https://yourbusiness.co.uk"
                {...register('businessWebsite')}
              />
            </div>
          </div>
        )}

        {/* Step 3: Tier selection */}
        {step === 3 && (
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              Choose your subscription plan. You can upgrade or downgrade at any time.
            </p>
            <div className="space-y-3 mb-5">
              {tiers.map((tier) => (
                <label
                  key={tier.id}
                  className={`flex items-center gap-4 p-4 rounded border-2 cursor-pointer transition-all ${
                    selectedTier === tier.value
                      ? `${tier.color} bg-primary/5`
                      : 'border-border hover:border-primary/30'
                  }`}
                >
                  <input
                    type="radio"
                    value={tier.value}
                    className="accent-primary"
                    {...register('tier')}
                  />
                  <div className="w-8 h-8 bg-muted rounded flex items-center justify-center shrink-0">
                    <tier.icon size={16} className="text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-foreground">{tier.label}</span>
                      <span className="text-xs font-bold text-primary">{tier.price}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{tier.desc}</span>
                  </div>
                  {selectedTier === tier.value && (
                    <Check size={16} className="text-primary shrink-0" />
                  )}
                </label>
              ))}
            </div>

            <div className="mb-5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-primary w-4 h-4 mt-0.5 shrink-0"
                  {...register('agreeTerms', { required: 'You must agree to the terms to register' })}
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  I agree to the{' '}
                  <span className="text-primary hover:underline cursor-pointer">Terms of Use</span>
                  ,{' '}
                  <span className="text-primary hover:underline cursor-pointer">Privacy Policy</span>
                  , and{' '}
                  <span className="text-primary hover:underline cursor-pointer">Business Listing Guidelines</span>
                  {' '}of the West Suffolk Business Directory.
                </span>
              </label>
              {errors.agreeTerms && <p className="text-negative text-xs mt-1.5">{errors.agreeTerms.message}</p>}
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex gap-3 mt-6">
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className="btn-secondary flex-1 justify-center text-sm"
            >
              <ChevronLeft size={14} />
              Back
            </button>
          )}
          {step < 3 ? (
            <button
              type="button"
              onClick={nextStep}
              className="btn-primary flex-1 justify-center text-sm"
            >
              Continue
              <ChevronRight size={14} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary flex-1 justify-center text-sm py-3"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Registering...
                </>
              ) : (
                'Create Business Listing'
              )}
            </button>
          )}
        </div>
      </form>

      <p className="text-sm text-muted-foreground text-center mt-5">
        Already have an account?{' '}
        <button onClick={onSwitchToLogin} className="text-primary font-semibold hover:underline">
          Sign in
        </button>
      </p>
    </div>
  );
}